import React from 'react';
import { Button } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoArrowBack } from 'react-icons/io5';
import PropTypes from 'prop-types';

// Map URL prefixes (most specific first) to display names.
// Used to derive the back button label dynamically from returnUrl.
const ROUTE_LABELS = [
  { prefix: '/admin/price-strategy/details', label: 'Price Strategy' },
  { prefix: '/admin/price-strategy', label: 'Price Strategies' },
  { prefix: '/admin/merchants/details', label: 'Merchant' },
  { prefix: '/admin/merchants', label: 'Merchants' },
  { prefix: '/admin/stations/details', label: 'Station' },
  { prefix: '/admin/stations', label: 'Stations' },
  { prefix: '/admin/users/details', label: 'User' },
  { prefix: '/admin/users', label: 'Users' },
  { prefix: '/admin/admin-users/details', label: 'Admin User' },
  { prefix: '/admin/admin-users', label: 'Admin Users' },
  { prefix: '/admin/powerbanks/details', label: 'Power Bank' },
  { prefix: '/admin/powerbanks', label: 'Power Banks' },
  { prefix: '/admin/orders/details', label: 'Order' },
  { prefix: '/admin/orders', label: 'Orders' },
  { prefix: '/admin/coupons/details', label: 'Coupon' },
  { prefix: '/admin/coupons', label: 'Coupons' },
  { prefix: '/admin/e-invoices/merchant-details', label: 'Invoice' },
  { prefix: '/admin/e-invoices/customer-details', label: 'Invoice' },
  { prefix: '/admin/e-invoices', label: 'E-Invoices' },
  { prefix: '/admin/settlements', label: 'Settlements' },
  { prefix: '/admin/profitability-analysis', label: 'Profitability Analysis' },
];

function getLabelFromUrl(url) {
  const path = url.split('?')[0];
  const match = ROUTE_LABELS.find((r) => path.startsWith(r.prefix));
  return match ? `Back to ${match.label}` : null;
}

/**
 * BackButton - Reusable back navigation component for detail pages.
 *
 * Reads `location.state.returnUrl` to navigate back to the exact page the
 * user came from, preserving all search params and filters.
 * Derives the button label dynamically from returnUrl (e.g. "Back to Merchant",
 * "Back to Price Strategy"). Falls back to the `label` prop when no returnUrl
 * is available (e.g. direct URL access).
 *
 * @param {string} fallbackUrl - The default route to navigate to when no returnUrl in state
 * @param {string} label - Fallback button text used when there is no returnUrl
 */
export default function BackButton({ fallbackUrl, label = 'Back' }) {
  const location = useLocation();
  const navigate = useNavigate();

  const returnUrl = location.state?.returnUrl;
  const returnUrlState = location.state?.returnUrlState;
  const displayLabel = returnUrl ? getLabelFromUrl(returnUrl) || label : label;

  const handleBack = () => {
    if (returnUrl) {
      navigate(returnUrl, { state: returnUrlState || null });
    } else {
      navigate(fallbackUrl);
    }
  };

  return (
    <Button
      variant="ghost"
      leftIcon={<IoArrowBack size={18} />}
      onClick={handleBack}
      fontWeight="medium"
      fontSize="sm"
      color="gray.600"
      _hover={{ color: 'gray.900', bg: 'gray.100' }}
      px="12px"
      py="8px"
      borderRadius="8px"
      size="sm"
    >
      {displayLabel}
    </Button>
  );
}

BackButton.propTypes = {
  fallbackUrl: PropTypes.string.isRequired,
  label: PropTypes.string,
};
